import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    isAuthLoginRequest,
    isConfigRequest,
    isDevicesRequest,
    isHealthRequest,
    isRfidLoginRequest,
    isTelemedicineRequest,
    isTokenRefreshRequest,
} from '@mona/api';
import { Logger } from '@mona/shared/logger';
import { AuthActions } from '../state';

/**
 * Cancel in-flight `/pdms/*` (and other authenticated data) requests as soon as the
 * user-initiated logout begins.
 *
 * Why: When the user logs out, the server soft-deletes the current PractitionerToken.
 * Any request that was dispatched with that token but had not yet been answered will
 * come back as 401 after logout — and the existing TokenInterceptor will try to refresh
 * or logout again, leading to a "white screen with infinite spinner" because the UI
 * keeps waiting on the stale request and also keeps displaying the lockscreen on top.
 *
 * The safest fix is to stop those in-flight requests the moment we know the user logged
 * out. Angular's HttpClient cancels the underlying XHR when the observable is
 * unsubscribed, so `takeUntil(abort$)` is enough.
 *
 * IMPORTANT — only abort on `logOut`. We deliberately do NOT abort on
 * `logInWithRfid` / `logInWithCredentials`, even when a user is already authenticated.
 * The earlier "user-switch" abort heuristic (gated on `isAuthenticated`) misfired on
 * lock-screen idle-unlock and on `AuthFlow.verify` confirmation flows used by
 * `persistChanges$` and discard-changes dialogs — both dispatch `logInWithRfid` while
 * the same user is still authenticated. That misfire silently cancelled in-flight
 * `POST /pdms/changes/...` saves; because `makeDefaultAsyncActionEffect` does not
 * emit a terminal action when the source observable completes without a value, the
 * change-log slice's `inProgress: true` stayed sticky and downstream effects waiting
 * on `getSaveChangeAction()` (medication prescriptions, prescription sets, care
 * reports, …) hung forever, surfacing as the "endless save spinner that gets worse
 * over time and only goes away after a Mona restart" reported on prod terminals.
 * The original "stale 401 after user-switch" symptom is already mitigated by the
 * post-logout 401 silence window in {@link TokenInterceptor}, so dropping the login
 * triggers from this interceptor is safe.
 *
 * Exclusions: requests that MUST complete regardless of the logout transition are
 * excluded (auth login/rfid/refresh/logout itself, config bootstrapping, device
 * provisioning, health checks, telemedicine). Everything else (all `/pdms/*` data,
 * changelog, permissions, etc.) is cancelled.
 *
 * This interceptor must run BEFORE {@link TokenInterceptor} in the chain so that
 * aborted requests never reach the refresh/logout branches.
 */
@Injectable()
export class HttpAbortOnLogoutInterceptor implements HttpInterceptor {
    private logger = new Logger('AUTH:ABORT');

    /**
     * Fires only on user-initiated `logOut`. Login transitions (including user-switch
     * and lock-screen unlock) MUST NOT trigger this — see class JSDoc for the
     * regression that motivated narrowing the trigger set.
     */
    private readonly abort$ = new Subject<void>();

    /**
     * Constructor
     *
     * @param actionsObserver$
     */
    constructor(private actionsObserver$: ActionsSubject) {
        this.actionsObserver$.pipe(ofType(AuthActions.logOut)).subscribe(action => {
            this.logger.log(`aborting in-flight data requests on ${action.type}`);
            this.abort$.next();
        });
    }

    /**
     * Intercept
     *
     * @param request
     * @param next
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.shouldSkip(request)) {
            return next.handle(request);
        }
        return next.handle(request).pipe(takeUntil(this.abort$));
    }

    private shouldSkip(request: HttpRequest<any>): boolean {
        return (
            isAuthLoginRequest(request) ||
            isRfidLoginRequest(request) ||
            isTokenRefreshRequest(request) ||
            isConfigRequest(request) ||
            isHealthRequest(request) ||
            isDevicesRequest(request) ||
            isTelemedicineRequest(request) ||
            (request.url || '').includes('/auth/user/logout')
        );
    }
}
